// frontend/src/components/asset/SecureFileLink.js
/**
 * Opens an authenticated file endpoint in a new tab.
 *
 * Asset documents are served behind JWT-protected DRF endpoints, so a plain
 * <a href> would 401 — the browser doesn't attach the Authorization header.
 * This fetches the file as a blob through the axios client (interceptors add
 * the token) and opens the resulting object URL.
 */

import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import api from '../../services/api';
import toast from '../../utils/toast';

const SecureFileLink = ({
  url,
  label = 'View Document',
  icon: Icon = FileText,
  theme = 'admin',
  className = '',
}) => {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setLoading(true);
    try {
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // The new tab holds its own reference, so the URL can be released after
      // the browser has had a tick to load it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      const status = error?.response?.status;
      toast.error(status === 404 ? 'No file was uploaded.' : 'Could not open the file.');
    } finally {
      setLoading(false);
    }
  };

  const btnClass = theme === 'admin' ? 'btn btn-admin-outline' : 'btn btn-outline';

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={loading}
      className={`${btnClass} ${className}`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
      ) : (
        <Icon className="w-4 h-4 mr-2" />
      )}
      {label}
    </button>
  );
};

export default SecureFileLink;
